import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, PriceInput } from '@artosera/ui';
import {
  useComptesBancaires,
  useReglagesTresorerie,
  useCreerCompteBancaire,
  useModifierCompteBancaire,
  useModifierReglagesTresorerie,
  messageTresorerie,
  type CorpsCompte,
  type CompteBancaire,
  type ReglagesTresorerie,
} from '../../api/tresorerie.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { TresorerieCadre, EtatRequete, aujourdhui, dateParis } from '../tresorerie/commun.js';
import styles from '../tresorerie/Tresorerie.module.css';
function FormulaireCompte({ compte, fermer }: { compte?: CompteBancaire; fermer: () => void }) {
  const { galerieSlug } = useShellContext();
  const { t, i18n } = useTranslation('regie');
  const creer = useCreerCompteBancaire(galerieSlug);
  const modifier = useModifierCompteBancaire(galerieSlug);
  const [corps, setCorps] = useState<CorpsCompte>(
    compte
      ? {
          libelle: compte.libelle,
          iban: compte.iban,
          bic: compte.bic,
          devise: 'EUR',
          compteComptable: compte.compteComptable,
          soldeOuvertureCentimes: compte.soldeOuvertureCentimes,
          soldeOuvertureLe: dateParis(compte.soldeOuvertureLe),
          ferme: compte.ferme,
        }
      : {
          libelle: '',
          iban: '',
          bic: '',
          devise: 'EUR',
          compteComptable: '',
          soldeOuvertureCentimes: 0,
          soldeOuvertureLe: aujourdhui(),
          ferme: false,
        },
  );
  const [erreur, setErreur] = useState('');
  const label = (c: string) => t(`tresorerie.${c}`);
  async function soumettre() {
    setErreur('');
    try {
      if (compte) {
        const { iban: _iban, devise: _devise, ...modification } = corps;
        await modifier.mutateAsync({ id: compte.id, corps: modification });
      } else await creer.mutateAsync(corps);
      fermer();
    } catch (e) {
      setErreur(messageTresorerie(e));
    }
  }
  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        void soumettre();
      }}
    >
      {(['libelle', 'iban', 'bic', 'compteComptable'] as const).map((c) => (
        <Field key={c} htmlFor={`compte-${c}`} label={label(c)}>
          <Input
            id={`compte-${c}`}
            required={c === 'libelle' || c === 'iban'}
            disabled={c === 'iban' && !!compte}
            value={corps[c]}
            onChange={(e) => setCorps({ ...corps, [c]: e.target.value })}
          />
        </Field>
      ))}
      <Field htmlFor="compte-solde" label={label('soldeOuverture')}>
        <PriceInput
          id="compte-solde"
          allowNegative
          cents={corps.soldeOuvertureCentimes}
          onCentsChange={(v) => setCorps({ ...corps, soldeOuvertureCentimes: v })}
          lang={i18n.language === 'en' ? 'en' : 'fr'}
        />
      </Field>
      <Field htmlFor="compte-date" label={label('dateOuverture')}>
        <Input
          id="compte-date"
          type="date"
          required
          value={corps.soldeOuvertureLe}
          onChange={(e) => setCorps({ ...corps, soldeOuvertureLe: e.target.value })}
        />
      </Field>
      {compte && (
        <label>
          <input
            type="checkbox"
            checked={corps.ferme}
            onChange={(e) => setCorps({ ...corps, ferme: e.target.checked })}
          />{' '}
          {label('compteFerme')}
        </label>
      )}
      {erreur && <p role="alert">{erreur}</p>}
      <Button type="submit" loading={creer.isPending || modifier.isPending}>
        {label('enregistrerCompte')}
      </Button>
    </form>
  );
}
function FormulaireReglages({ reglages }: { reglages: ReglagesTresorerie }) {
  const { galerieSlug } = useShellContext();
  const { t, i18n } = useTranslation('regie');
  const modifier = useModifierReglagesTresorerie(galerieSlug);
  const numeriques = [
    'delaiEncaissementPipelineJours',
    'delaiReversementJours',
    'echeanceParDefautJours',
    'joursAvantEcart',
    'ecartMaxPdb',
  ] as const;
  const [corps, setCorps] = useState<ReglagesTresorerie>({
    delaiEncaissementPipelineJours: reglages.delaiEncaissementPipelineJours,
    delaiReversementJours: reglages.delaiReversementJours,
    echeanceParDefautJours: reglages.echeanceParDefautJours,
    joursAvantEcart: reglages.joursAvantEcart,
    ecartMaxPdb: reglages.ecartMaxPdb,
    ecartMaxCentimes: reglages.ecartMaxCentimes,
    seuilAlerteCentimes: reglages.seuilAlerteCentimes,
  });
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState(false);
  const label = (c: string) => t(`tresorerie.${c}`);
  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        setErreur('');
        setSucces(false);
        void modifier
          .mutateAsync(corps)
          .then(() => setSucces(true))
          .catch((e: unknown) => setErreur(messageTresorerie(e)));
      }}
    >
      <h2>{label('delaisSeuils')}</h2>
      {numeriques.map((c) => (
        <Field key={c} htmlFor={`reglage-${c}`} label={label(c)}>
          <Input
            id={`reglage-${c}`}
            type="number"
            required
            min={0}
            max={c === 'ecartMaxPdb' ? 2000 : 365}
            step={1}
            value={corps[c]}
            onChange={(e) => setCorps({ ...corps, [c]: Number(e.target.value) })}
          />
        </Field>
      ))}
      {(['seuilAlerteCentimes', 'ecartMaxCentimes'] as const).map((c) => (
        <Field key={c} htmlFor={`reglage-${c}`} label={label(c)}>
          <PriceInput
            id={`reglage-${c}`}
            allowNegative={c === 'seuilAlerteCentimes'}
            cents={corps[c]}
            onCentsChange={(v) => setCorps({ ...corps, [c]: v })}
            lang={i18n.language === 'en' ? 'en' : 'fr'}
          />
        </Field>
      ))}
      {erreur && <p role="alert">{erreur}</p>}
      {succes && <p role="status">{label('enregistre')}</p>}
      <Button type="submit" loading={modifier.isPending}>
        {label('enregistrerReglages')}
      </Button>
    </form>
  );
}
function Contenu() {
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation('regie');
  const comptes = useComptesBancaires(galerieSlug);
  const reglages = useReglagesTresorerie(galerieSlug);
  const [compte, setCompte] = useState<CompteBancaire | 'nouveau'>();
  const label = (c: string) => t(`tresorerie.${c}`);
  return (
    <>
      <h2>{label('comptes')}</h2>
      <EtatRequete requete={comptes} />
      <Button onClick={() => setCompte('nouveau')}>{label('nouveauCompte')}</Button>
      <ul>
        {comptes.data?.comptes.map((c) => (
          <li key={c.id}>
            {c.libelle} · {c.iban} · {label(c.ferme ? 'ferme' : 'ouvert')}{' '}
            <Button onClick={() => setCompte(c)}>{label('modifier')}</Button>
          </li>
        ))}
      </ul>
      {compte && (
        <Dialog
          open
          onOpenChange={(v) => {
            if (!v) setCompte(undefined);
          }}
          title={label('compte')}
          closeLabel={label('fermer')}
        >
          <FormulaireCompte
            compte={compte === 'nouveau' ? undefined : compte}
            fermer={() => setCompte(undefined)}
          />
        </Dialog>
      )}
      <EtatRequete requete={reglages} />
      {reglages.data && <FormulaireReglages reglages={reglages.data.reglages} />}
    </>
  );
}
export function TresorerieReglagesPage() {
  return (
    <TresorerieCadre titre="reglages" reglages>
      <Contenu />
    </TresorerieCadre>
  );
}
